import React from "react";
import {
  type EventsPage,
  type EventsPageSimple,
  type EventSeries,
  getClient,
  getEventsPage,
  getEventPagesWithSeries,
  getEventSeries,
  groq,
  type Banner,
  getAnnouncementBanner,
} from "lib/sanity";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import NotFound from "pages/404";
import EventPageTemplate from "components/events/event-template";
import EventSeriesTemplate from "components/events/series-template";

type PageProps = {
  slug: string;
  eventPage?: EventsPage;
  seriesPage?: EventSeries;
  seriesPageEvents: EventsPageSimple[];
  announcementBanner?: Banner;
};

const EventPage: NextPage<Readonly<PageProps>> = ({
  slug,
  eventPage,
  seriesPage,
  seriesPageEvents,
  announcementBanner,
}) => {
  if (slug && eventPage) {
    return <EventPageTemplate {...eventPage} />;
  }

  if (slug && seriesPage) {
    return (
      <EventSeriesTemplate
        announcementBanner={announcementBanner}
        associatedEventPages={seriesPageEvents}
        {...seriesPage}
      />
    );
  }

  return <NotFound />;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(
    groq`*[_type == "eventsPage" || _type == "eventSeries"] { slug }`,
  );

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const slug = String(params?.slug);
  const [eventPage, seriesPage, announcementBanner] = await Promise.all([
    getEventsPage(slug),
    getEventSeries(slug),
    getAnnouncementBanner(),
  ]);

  if (!eventPage && !seriesPage) {
    return { notFound: true };
  }

  const seriesPageEvents = seriesPage ? await getEventPagesWithSeries(seriesPage.slug) : [];

  const props: PageProps = {
    slug,
    seriesPageEvents,
  };

  if (eventPage) props.eventPage = eventPage;
  if (seriesPage) props.seriesPage = seriesPage;
  if (announcementBanner) props.announcementBanner = announcementBanner;

  return {
    props,
    revalidate: 60,
  };
};

export default EventPage;
